import React from "react"
import { css } from "@emotion/react"
import { Theme } from "@myvp/shared/src/styles/theme"

export type IconElement = (props: IconProps) => React.ReactElement

export const ICON_SIZES = ["small", "medium", "large"] as const
type IconSize = (typeof ICON_SIZES)[number]
export const ICON_COLORS = [
  "default",
  "alt",
  "inverse",
  "link",
  "critical",
  "disabled",
  "highlight",
  "success",
  "warning",
] as const
export type IconColor = (typeof ICON_COLORS)[number]

export type IconProps = React.SVGProps<SVGSVGElement> & {
  className?: string
  size?: IconSize
  color?: IconColor
}

export const statusIconVariants = ["outlined", "filled", "subtle"] as const

export interface StatusIconProps extends Omit<IconProps, "color"> {
  variant: (typeof statusIconVariants)[number]
}

export const getIconViewBox = (size?: IconSize) => {
  switch (size) {
    case "small":
      return "0 0 16 16"
    case "medium":
      return "0 0 24 24"
    case "large":
    default:
      return "0 0 32 32"
  }
}

export const iconSizeCss = (size?: IconSize) => {
  let pxSize
  switch (size) {
    case "small":
      pxSize = "16px"
      break
    case "medium":
      pxSize = "24px"
      break
    case "large":
    default:
      pxSize = "32px"
      break
  }

  return css`
    width: ${pxSize};
    min-width: ${pxSize};
    height: ${pxSize};
    min-height: ${pxSize};
  `
}

const getPaletteColor = (theme: Theme, color?: IconColor) => {
  switch (color) {
    case "alt":
      return theme.palette.content.neutral.subtle
    case "critical":
      return theme.palette.data.critical["level-6"]
    case "disabled":
      return theme.palette.content.neutral.disabled
    case "highlight":
      return theme.palette.content.secondary.default
    case "inverse":
      return theme.palette.content.neutral.inverse
    case "link":
      return theme.palette.content.primary.default
    case "success":
      return theme.palette.data.success["level-6"]
    case "warning":
      return theme.palette.content.warning.default
    case "default":
    default:
      return theme.palette.content.neutral.default
  }
}

export const statusIconColorCss =
  (
    status: "critical" | "success" | "warning",
    variant: StatusIconProps["variant"]
  ) =>
  (theme: Theme) => {
    if (variant === "outlined") {
      return css`
        --textColor: ${theme.palette.content[status].default};
        --shapeStrokeColor: ${theme.palette.content[status].default};
      `
    } else if (variant === "subtle") {
      return css`
        --shapeFillColor: ${theme.palette.surface[status].strong};
        --textColor: ${theme.palette.content[status].strong};
        --shapeStrokeColor: ${theme.palette.surface[status].strong};
      `
    } else {
      return css`
        --shapeFillColor: ${theme.palette.content[status].default};
        --textColor: ${theme.palette.content.neutral.inverse};
        --shapeStrokeColor: ${theme.palette.content[status].default};
      `
    }
  }

export const iconColorCss =
  (color?: IconColor, cssVarName = "color") =>
  (theme: Theme) =>
    css`
      --${cssVarName}: ${getPaletteColor(theme, color)};
    `
